/**
 * Detects a failed lazy-chunk load.
 *
 * Used by the app-level and section-level error boundaries to distinguish a
 * transient chunk-load failure (worth a cache-busting `?retry=N` reload) from a
 * genuine render error (which a reload won't fix), and by the silent retry in
 * `@shared/core/chunkRetry` to decide whether to retry at all. Both read this
 * one function — a second copy would eventually drift, and a detector that is narrower than
 * the retry path means failures it misses never get retried.
 *
 * Matches:
 *  - `error.name === 'ChunkLoadError'` (webpack's JSONP runtime)
 *  - `"Loading chunk … failed"` (JS chunks — name sometimes lost across async boundaries)
 *  - `"Loading CSS chunk … failed"` (CssExtractRspackPlugin's stylesheet runtime, which sets
 *    `code`/`type`/`request` but leaves `name` at the default `Error`, so the name check alone
 *    never sees a CSS failure)
 *
 * The character class accepts `/` and `.` because joy-web's named chunks routinely use
 * path-style identifiers (`content/contactCollector/iframe`, `content/admin/photo-upload`),
 * and `~` because that is webpack's default splitChunks `automaticNameDelimiter`
 * (`vendors~app`) — no such chunk exists today, since every cacheGroup is named explicitly,
 * but this fallback should not quietly stop matching if that changes.
 *
 * @param error The caught error (nullable — boundaries hold `Error | null` state).
 * @returns `true` if the error looks like a chunk-load failure.
 */
export const isChunkLoadError = (error: Error | null): boolean => {
  if (!error) return false;
  return error.name === 'ChunkLoadError' || /loading (?:css )?chunk [\w./~-]+ failed\b/i.test(error.message ?? '');
};
